import { getDictationDataConfig, type T11nHttpClient } from '../config';

/**
 * Package-side façade over the injected transcription-service client.
 *
 * Deliberately named `t11nClient` on export and shaped exactly like the
 * app-side instance it replaces (`src/lib/SessionRecordingProvider/t11nClient.ts`),
 * so that every moved fn body keeps its call sites **byte-identical** across the
 * move — only the import specifier changes. That is what makes the five moved
 * bodies reviewable as a pure rename, which matters more than usual here because
 * the module they came from is a live-recording module owned by another pod.
 *
 * The client is LOADED on first use, not held. Three properties fall out of
 * that, and all three are load-bearing (the `loadInboxHttp` findings,
 * ARCFE-1016, inherited rather than re-derived):
 *
 * 1. **The app's client cone never reaches `_app` module scope.** On the
 *    integrations lane it did, and it reordered async-module settlement badly
 *    enough to TDZ the Rematch store during "Collecting page data". The
 *    t11nClient cone reaches the same store (through `getCurrentUser` and the
 *    sync auth-token helper), so the same hazard applies verbatim.
 * 2. **Bootstrap ORDER cannot matter.** A module-scope `const c = getConfig()`
 *    would run at first import and throw for anything imported before wiring.
 * 3. **An unconfigured call throws the loud `[dictation/data] not configured`
 *    error at the call**, rather than capturing a null and failing later as an
 *    opaque `undefined.get`.
 *
 * The loaded promise is memoised, so the dynamic import resolves once per
 * process no matter how many requests are in flight.
 *
 * ARITY IS FORWARDED EXACTLY, via rest-tuple spread rather than named
 * parameters. Writing `get: (url, config) => real.get(url, config)` turns a
 * one-argument call into a two-argument one with an explicit `undefined`, which
 * axios tolerates but every `toHaveBeenCalledWith(url)` assertion does not.
 * Spreading preserves `arguments.length`, so the client sees the same call it
 * saw before the move — and `deleteDictation` is exactly such a one-argument
 * call.
 */
let cached: Promise<T11nHttpClient> | null = null;
let cachedFor: (() => unknown) | null = null;

const client = (): Promise<T11nHttpClient> => {
  const { loadT11nHttp } = getDictationDataConfig();
  // Re-resolve if the config was replaced (tests swap doubles between cases);
  // keyed on the loader identity so production still resolves exactly once.
  if (cached === null || cachedFor !== loadT11nHttp) {
    cachedFor = loadT11nHttp;
    cached = Promise.resolve(loadT11nHttp());
  }
  return cached;
};

/** Test-only: drop the memoised client so a suite cannot inherit the previous one. */
export const resetT11nHttpCacheForTests = (): void => {
  cached = null;
  cachedFor = null;
};

export const t11nHttp: T11nHttpClient = {
  get: async <T>(
    ...args: [
      string,
      (
        | { params?: Record<string, string | number | undefined>; timeout?: number }
        | undefined
      )?,
    ]
  ) => (await client()).get<T>(...args),
  post: async <T>(...args: [string, unknown?]) => (await client()).post<T>(...args),
  put: async <T>(...args: [string, unknown?, { timeout?: number }?]) =>
    (await client()).put<T>(...args),
  delete: async <T>(...args: [string]) => (await client()).delete<T>(...args),
};
